import { EmptyState, ErrorState, FreshnessBar } from "../components/AdminPageStates";
import { useFounderCommunity } from "../hooks/useFounderCommunity";
import { FounderCommunityHighlight, FounderCommunityMetric, FounderCommunityStatus, FounderCommunityStory } from "../services/founderCommunityService";

const LOADING_CARDS = ["Active Dancers", "Messages Sent", "Bookings Completed", "Challenges Submitted", "Loves", "Follows"];

function statusLabel(status: FounderCommunityStatus) {
  switch (status) {
    case "healthy":
      return "Healthy";
    case "growing":
      return "Growing";
    case "watching":
      return "Watching";
    case "future":
      return "Future";
    case "unavailable":
      return "Unavailable";
  }
}

function CommunityStoryCard({ story }: { story: FounderCommunityStory }) {
  return (
    <section className="founder-community-story-card" aria-label={`${story.headline} ${story.insight}`}>
      <div className="founder-community-story-topline">
        <span className="founder-community-icon" aria-hidden="true">
          {story.icon}
        </span>
        <span className={`status-badge ${story.status}`}>{statusLabel(story.status)}</span>
      </div>
      <h2>{story.headline}</h2>
      <p>{story.insight}</p>
    </section>
  );
}

function CommunityMetricCard({ metric }: { metric: FounderCommunityMetric }) {
  return (
    <details className={`founder-community-metric-card ${metric.status}`}>
      <summary>
        <span>
          <span className="founder-community-metric-title">{metric.title}</span>
          <span className="founder-community-metric-story">{metric.story}</span>
        </span>
        <strong>{metric.value}</strong>
      </summary>
      <div className="founder-community-metric-detail">
        <p>{metric.detail}</p>
        <p>Source: {metric.source}</p>
        {metric.unavailableReason ? <p>{metric.unavailableReason}</p> : null}
      </div>
    </details>
  );
}

function HighlightCard({ highlight }: { highlight: FounderCommunityHighlight }) {
  return (
    <section className={`founder-community-highlight-card ${highlight.status}`}>
      <div>
        <p className="founder-community-metric-title">{highlight.title}</p>
        <h3>{highlight.value}</h3>
      </div>
      <p>{highlight.story}</p>
      <small>Source: {highlight.source}</small>
    </section>
  );
}

function LoadingCard({ title }: { title: string }) {
  return (
    <section className="founder-community-metric-card loading-card" aria-busy="true">
      <p className="founder-community-metric-title">{title}</p>
      <div className="skeleton skeleton-value" />
      <div className="skeleton skeleton-note" />
    </section>
  );
}

export default function FounderCommunity() {
  const { data, error, isLoading, lastUpdated, refresh } = useFounderCommunity();

  if (error) {
    return <ErrorState message="Unable to load Founder Community. Check Firebase permissions and aggregate access." />;
  }

  if (!isLoading && !data) {
    return <EmptyState message="Community engagement will appear as dancers, coaches, studios, and guardians interact." />;
  }

  return (
    <section className="founder-community-page" aria-label="Founder Community">
      <FreshnessBar
        dataSource="Read-only community queries from users, conversations/messages, bookings, submissions, comments, postLikes, challengeSubmissionLoves, follows, guardianLinks, challenges, and classSessions"
        isLive={false}
        lastUpdated={lastUpdated}
        onRefresh={() => {
          void refresh();
        }}
      />

      <header className="founder-community-hero">
        <p className="eyebrow">Founder community</p>
        <h2>Is Chor's dance community healthy?</h2>
        <p>
          Community health is shown through participation, relationships, conversation, bookings, guardian connection,
          and challenge energy. Each card tells one engagement story.
        </p>
      </header>

      {isLoading || !data ? (
        <div className="founder-community-metric-grid">
          {LOADING_CARDS.map((title) => (
            <LoadingCard key={title} title={title} />
          ))}
        </div>
      ) : (
        <>
          <CommunityStoryCard story={data.hero} />

          <div className="founder-community-story-grid" aria-label="Community engagement stories">
            {data.stories.map((story) => (
              <CommunityStoryCard key={story.id} story={story} />
            ))}
          </div>

          <section className="founder-community-section" aria-labelledby="community-highlights-heading">
            <div className="founder-community-section-header">
              <p className="eyebrow">Community highlights</p>
              <h3 id="community-highlights-heading">Who is creating momentum?</h3>
            </div>
            <div className="founder-community-highlight-grid">
              {data.highlights.map((highlight) => (
                <HighlightCard key={highlight.id} highlight={highlight} />
              ))}
            </div>
          </section>

          <section className="founder-community-section" aria-labelledby="community-signals-heading">
            <div className="founder-community-section-header">
              <p className="eyebrow">Engagement signals</p>
              <h3 id="community-signals-heading">What is happening in the community?</h3>
            </div>
            <div className="founder-community-metric-grid">
              {data.metrics.map((metric) => (
                <CommunityMetricCard key={metric.id} metric={metric} />
              ))}
            </div>
          </section>
        </>
      )}
    </section>
  );
}
